<template>
  <div class="px-4 flex items-center absolute w-full h-full top-0 left-0 bg-black/50">
    <div
      class="max-w-md mx-auto text-center bg-[#F9FAFC] px-4 sm:px-8 py-10 rounded-xl shadow"
    >
      <header class="mb-8">
        <h1 class="text-2xl sm:text-4xl font-bold mb-1">Email Verification</h1>
        <p class="text-base text-gray-400">
          Enter the 4-digit verification, within 3 minutes, code that was sent to your
          email: {{ email }}
        </p>
      </header>

        <form @submit="sendCodeOtp">
          <div class="flex items-center justify-center gap-3 mb-8">
            <!-- Replaced repeated inputs with a v-for loop -->
            <input
              v-for="(digit, index) in codeDigits"
              :key="index"
              type="text"
              v-model="codeDigits[index]"
              :ref="(el) => (inputRefs[index] = el)"
              class="w-14 h-14 text-center text-2xl font-bold text-slate-900 bg-slate-100 border border-transparent hover:border-slate-200 appearance-none rounded p-4 outline-none focus:bg-white focus:border-principal-500"
              maxlength="1"
              inputmode="numeric"
              @keydown="handleKeyDown($event, index)"
              @input="handleInput($event, index)"
              @focus="handleFocus"
              @paste="handlePaste"
            />
          </div>
          <!-- Display OTP error message -->
          <div v-if="otpError" class="text-red-500 text-sm mb-4">
            {{ otpError }}
          </div>
          <div class="max-w-[260px] mx-auto">
            <!-- <button
              ref="submitRef"
              type="submit"
              class="w-full cursor-pointer inline-flex justify-center whitespace-nowrap rounded-lg bg-principal-500 px-3.5 py-2.5 text-sm font-medium text-black shadow-sm shadow-indigo-950/10 hover:bg-principal-700 focus:outline-none focus:ring focus:ring-principal-300 focus-visible:outline-none focus-visible:ring focus-visible:ring-principal-300 transition-colors duration-150"
            >
              Verify Account
            </button> -->
            <ButtonComponent
              :loading="loading"
              :class="[
                'btn-primary',
                'w-full',
                'flex',
                'gap-y-2',
                'justify-center',
                'items-center',
              ]"
            />
          </div>
        </form>
        <div class="text-sm text-slate-500 mt-4 flex flex-col gap-y-2">
          <span>
            Didn't receive code?
            <button
              @click="resendOTP"
              :disabled = "sendingCodeOTP"
              class="font-medium text-black hover:font-semibold cursor-pointer"
              >Resend</button
            >
          </span>
          <span class="cursor-pointer font-sm text-black pt-4" @click="back">Back</span>
        </div>

    </div>
  </div>

  <MessageComponent />
</template>

<script setup>
import { ref, reactive, defineEmits, defineProps, onMounted} from "vue";
import ButtonComponent from "./ButtonComponent.vue";
import MessageComponent from "./MessageComponent.vue";
import { useMessage } from "../services/messageServices";
import axios from "axios";

const messagesService = useMessage();

const codeDigits = reactive(["", "", "", ""]);
const emailInserted = ref(false);
const inputRefs = ref([]);
const submitRef = ref(null);
const otpError = ref("");
const loading = ref(false);
const sendingCodeOTP = ref(false);

const props = defineProps({
  email: {
    type: String,
    required: true,
  },
})



const emit = defineEmits(["next","back"]);

const back = () => {
  emit("back"); 
};

const sendCodeOtp = async (e) => {
  loading.value = true;
  e.preventDefault();
  const otpValue = codeDigits.join("");

  if (!otpValue || otpValue.length !== 4 || !otpValue.includes("")) {
    otpError.value = "Enter 4 digits";
    return;
  }

  otpError.value = "";

  try {
    await axios.put(
      `${process.env.VUE_APP_ENDPOINT_API}/code-otp/verify-otp`,
      {
        email: sessionStorage.getItem("email"),
        otp: otpValue,
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    emit("next", { email: sessionStorage.getItem("email") });
  } catch (error) {
    console.error(
      "Error Status:",
      error.response.status,
      "- Error Message: ",
      error.response.data.error
    );
    if (error.response.status === 403) {
      messagesService.error(
        "The code entered is invalid. Please try by cliking on the 'Resend'"
      );
    } else if (error.response.status == 500) {
      messagesService.error("An error occured while getting an OTP code");
    }
  } finally {
    loading.value = false;
  }
};

onMounted(async ()=>{
  await getCodeOTP();
})

const getCodeOTP =  async () => {

  try {
    sendingCodeOTP.value = true;
    await axios.post(
      `${process.env.VUE_APP_ENDPOINT_API}/code-otp/create-otp`,
      { email: props.email},
      { headers: { "Content-Type": "application/json" } }
    );
    emailInserted.value = true;
    
  } catch (error) {
    console.error(error);
    if (error.response.status === 409) {
      messagesService.error("An code has already been sent to your email");
    } else {
      messagesService.error("An error happened with sending the email");
    }
  }finally{
    sessionStorage.setItem("email", props.email);
    sendingCodeOTP.value = false;
  }
};

const resendOTP = async () => {
  try {
    sendingCodeOTP.value = true;
    await axios.post(
      `${process.env.VUE_APP_ENDPOINT_API}/code-otp/resend`,
      { email: sessionStorage.getItem("email") },
      { headers: { "Content-Type": "application/json" } }
    );
    messagesService.success("The code has been sent to your email");
  } catch (error) {
    console.error(
      "Error Status:",
      error.response.status,
      "- Error Message: ",
      error.response.data.error
    );
    messagesService.error("An error occured while resending the OTP code");
  }
  finally{
    sendingCodeOTP.value = false;
  }
};

const handleKeyDown = (e, index) => {
  if (
    !/^[0-9]$/.test(e.key) &&
    e.key !== "Backspace" &&
    e.key !== "Delete" &&
    e.key !== "Tab" &&
    !e.metaKey
  ) {
    e.preventDefault();
  }

  // Gestione del tasto Backspace
  if (e.key === "Backspace" || e.key === "Delete") {
    e.preventDefault(); // Previeni il comportamento di default
    if (codeDigits[index]) {
      // Se c'è un numero nella posizione corrente, cancellalo
      codeDigits[index] = "";
    } else if (index > 0) {
      // Se non c'è un numero e non siamo al primo input, vai all'input precedente
      codeDigits[index - 1] = "";
      inputRefs.value[index - 1].focus();
    }
  }
};

const handleInput = (e, index) => {
  const value = e.target.value;
  // Assicurati che l'input sia un singolo numero
  if (/^[0-9]$/.test(value)) {
    codeDigits[index] = value;
    if (index < codeDigits.length - 1) {
      inputRefs.value[index + 1].focus();
    } else {
      submitRef.value?.focus();
    }
  } else {
    codeDigits[index] = "";
  }
};

const handleFocus = (e) => {
  e.target.select();
};

const handlePaste = (e) => {
  e.preventDefault();
  const text = e.clipboardData.getData("text");
  if (!new RegExp(`^[0-9]{${codeDigits.length}}$`).test(text)) {
    return;
  }
  text.split("").forEach((digit, i) => {
    codeDigits[i] = digit;
  });
  submitRef.value?.focus();
};
</script>
